<div class="pts">
  @if (loading()) {
    <div class="pts__loading" data-testid="provider-tokens-loading">
      <mat-progress-spinner mode="indeterminate" diameter="32" />
    </div>
  } @else if (loadError()) {
    <div class="pts__error" role="alert">
      <span>{{ 'providerTokens.loadError' | translate }}</span>
      <button type="button" class="pts__retry" data-testid="provider-tokens-retry" (click)="retry()">
        {{ 'common.retry' | translate }}
      </button>
    </div>
  } @else {
    @if (hasSlots()) {
      @if (missingCount() > 0) {
        <div class="pts__banner" role="status" aria-live="polite" data-testid="provider-tokens-warning">
          <mat-icon class="pts__banner-icon" aria-hidden="true">shield</mat-icon>
          <span class="pts__banner-text">
            {{ 'providerTokens.banner.prefix' | translate }}
            <strong>{{ missingCount() }}</strong>
            {{
              (missingCount() === 1 ? 'providerTokens.banner.suffixOne' : 'providerTokens.banner.suffixOther')
                | translate
            }}
          </span>
        </div>
      }

      <div class="pts__card">
        <div class="pts__head">
          <div class="pts__head-host">{{ 'providerTokens.col.host' | translate }}</div>
          <div class="pts__head-status">{{ 'providerTokens.col.status' | translate }}</div>
          <div class="pts__head-action"></div>
        </div>

        @for (row of rows(); track row.slot.providerType + '::' + row.slot.host; let last = $last) {
          <div
            #zeile
            class="pts__row"
            [class.pts__row--missing]="!row.slot.tokenSet"
            [class.pts__row--fokus]="istFokussiert(row.slot)"
            [class.pts__row--last]="last"
            [attr.data-host]="row.slot.host"
            data-testid="provider-token-row"
          >
            <div class="pts__row-head">
              <div class="pts__tile pts__tile--{{ providerLabel(row.slot.providerType) }}" aria-hidden="true">
                @if (row.slot.providerType === 'JIRA') {
                  <i class="fa-brands fa-jira"></i>
                } @else if (row.slot.providerType === 'REDMINE') {
                  <span class="pts__tile-label">RM</span>
                } @else {
                  <i class="fa-brands fa-github"></i>
                }
              </div>

              <div class="pts__host-col">
                <div class="pts__host-line">
                  <span class="pts__badge pts__badge--{{ providerLabel(row.slot.providerType) }}">
                    {{ 'providerTokens.provider.' + providerLabel(row.slot.providerType) | translate }}
                  </span>
                  <code class="pts__host">{{ row.slot.host }}</code>
                </div>
                <div class="pts__projects">
                  {{
                    projectsKey(row.slot.projects)
                      | translate: { count: row.slot.projects.length, names: joinProjectNames(row.slot.projects) }
                  }}
                </div>
              </div>

              <div class="pts__status">
                @if (row.slot.tokenSet) {
                  <span class="pts__pill pts__pill--set">
                    <span class="pts__dot"></span>
                    {{ 'providerTokens.status.set' | translate }}
                  </span>
                  <small class="pts__meta">
                    {{
                      'providerTokens.status.lastUsed'
                        | translate: { when: row.slot.lastUsedAt ? (row.slot.lastUsedAt | date: 'dd.MM.yyyy') : '—' }
                    }}
                  </small>
                } @else {
                  <span class="pts__pill pts__pill--missing">
                    <span class="pts__dot"></span>
                    {{ 'providerTokens.status.missing' | translate }}
                  </span>
                  <small class="pts__meta pts__meta--warning">
                    {{ 'providerTokens.status.blocked' | translate }}
                  </small>
                }
              </div>

              <div class="pts__action-col">
                <button
                  type="button"
                  class="pts__action"
                  [class.pts__action--set]="row.slot.tokenSet"
                  [class.pts__action--missing]="!row.slot.tokenSet"
                  [attr.aria-expanded]="row.expanded"
                  [attr.aria-label]="actionAriaKey(row) | translate: { host: row.slot.host }"
                  (click)="toggleRow(row)"
                >
                  {{ actionLabelKey(row) | translate }}
                </button>
              </div>
            </div>

            @if (row.expanded) {
              <div class="pts__expand">
                <div class="pts__panel">
                  <div class="pts__expand-head">
                    <mat-icon aria-hidden="true">key</mat-icon>
                    <span>
                      {{ 'providerTokens.expand.titlePrefix' | translate }}
                      <code class="pts__expand-host">{{ row.slot.host }}</code>
                    </span>
                  </div>
                  <div class="pts__expand-row">
                    <input
                      type="password"
                      data-testid="provider-token-input"
                      autocomplete="off"
                      class="pts__input"
                      [attr.aria-label]="'providerTokens.expand.inputLabel' | translate: { host: row.slot.host }"
                      [attr.placeholder]="
                        (row.slot.tokenSet
                          ? 'providerTokens.expand.placeholderRotate'
                          : 'providerTokens.expand.placeholder'
                        ) | translate
                      "
                      [value]="row.draft"
                      (input)="onDraftInput(row, $any($event.target).value)"
                    />
                    <button
                      type="button"
                      class="pts__save"
                      data-testid="provider-token-save"
                      [disabled]="!row.draft.trim() || row.busy"
                      (click)="save(row)"
                    >
                      {{ 'providerTokens.expand.save' | translate }}
                    </button>
                    @if (row.slot.tokenSet) {
                      <button
                        type="button"
                        class="pts__delete"
                        data-testid="provider-token-delete"
                        [disabled]="row.busy"
                        (click)="remove(row)"
                      >
                        {{ 'providerTokens.expand.delete' | translate }}
                      </button>
                    }
                  </div>
                  <div class="pts__expand-foot">
                    <span>{{ providerHelpKey(row.slot.providerType) | translate }}</span>
                    @if (row.slot.tokenSet && row.slot.rotatedAt) {
                      <span class="pts__rotated">
                        {{
                          'providerTokens.expand.rotatedAt'
                            | translate: { when: row.slot.rotatedAt | date: 'dd.MM.yyyy' }
                        }}
                      </span>
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }

    <p class="pts__foot">
      <mat-icon aria-hidden="true">check</mat-icon>
      <span class="pts__foot-text">{{ 'providerTokens.footNote' | translate }}</span>
    </p>
  }
</div>
